/**
 * Mermaid syntax highlighting and folding for CodeMirror 6 — SPEC.md §5,
 * §12 M3; issue #9 "Implementation Decisions", issue #10.
 *
 * There's no first-class CodeMirror Mermaid language package, so this is a
 * hand-written `StreamParser`. Pure and DOM-free like `render.ts`/
 * `viewport.ts`/`pinch.ts`: it's driven directly by CodeMirror's
 * `StringStream`/`EditorState`, never an `EditorView`.
 *
 * M4's `diagramTypes.ts` registry doesn't exist yet, so the keyword list
 * here is self-contained and hardcoded — a known, accepted duplication with
 * the registry once M4 lands, not built preemptively.
 *
 * Coverage is imperfect by design (SPEC.md §5): stability — no flicker, no
 * throw on unfamiliar input — is the bar, not exhaustive grammar coverage
 * across all 27 diagram families.
 */
import type { EditorState } from '@codemirror/state';
import type { StreamParser, StringStream } from '@codemirror/language';

const KEYWORDS = [
  'flowchart',
  'graph',
  'sequenceDiagram',
  'classDiagram',
  'stateDiagram-v2',
  'stateDiagram',
  'erDiagram',
  'journey',
  'gantt',
  'pie',
  'quadrantChart',
  'requirementDiagram',
  'gitGraph',
  'C4Context',
  'C4Container',
  'C4Component',
  'C4Dynamic',
  'C4Deployment',
  'mindmap',
  'timeline',
  'sankey-beta',
  'sankey',
  'xychart-beta',
  'block-beta',
  'packet-beta',
  'kanban',
  'architecture-beta',
  'radar-beta',
  'treemap-beta',
  'zenuml',
  'participant',
  'actor',
  'loop',
  'alt',
  'else',
  'opt',
  'par',
  'and',
  'critical',
  'rect',
  'note',
  'activate',
  'deactivate',
  'state',
  'class',
  'section',
  'commit',
  'branch',
  'checkout',
  'merge',
  'title',
  'subgraph',
  'end',
];

const KEYWORD_PATTERN = new RegExp(`^(${KEYWORDS.join('|')})\\b`);

// Each alternative is a complete literal, so there's no prefix ambiguity
// between e.g. '-->' and '---' — order doesn't matter for correctness, only
// for readability.
const ARROW_PATTERN = /^(<-->|-\.->|-->|---|--x|--o|==>)/;

// Node shapes ([], (), {}, [[]], [()], (()), >], {{}}, [/], [\]) are all
// built from this fixed set of delimiter characters. Eating a whole run of
// them as one 'bracket' token per contiguous run — rather than parsing each
// compound shape's exact grammar — is the "imperfect is fine" choice SPEC.md
// §5 explicitly allows.
const BRACKET_PATTERN = /^[[\]{}()<>/\\]+/;

const STRING_PATTERN = /^"(?:[^"\\]|\\.)*"/;

// Characters that could start a bracket run, a string, or an arrow. The
// fallback below must stop at the first one of these it sees, even mid-word
// — otherwise a plain identifier like "A" swallows straight through an
// adjacent "[" or opening quote (both non-whitespace) before a later
// token() call ever gets a chance to recognize them on their own.
const FALLBACK_STOP_PATTERN = /[\s[\]{}()<>/\\"=-]/;

export interface MermaidTokenizerState {
  inFrontmatter: boolean;
}

export const mermaidStreamParser: StreamParser<MermaidTokenizerState> = {
  name: 'mermaid',

  startState(): MermaidTokenizerState {
    return { inFrontmatter: false };
  },

  token(stream: StringStream, state: MermaidTokenizerState): string | null {
    // A line that's exactly '---' toggles frontmatter mode — simple and
    // symmetric for both the opening and closing delimiter. Real frontmatter
    // only ever appears at the very top of a document, but a second stray
    // '---' later is rare enough in real Mermaid source that a false toggle
    // there is an acceptable "imperfect is fine" cost (SPEC.md §5).
    if (stream.sol() && stream.match(/^---\s*$/)) {
      state.inFrontmatter = !state.inFrontmatter;
      return 'meta';
    }
    if (state.inFrontmatter) {
      stream.skipToEnd();
      return 'meta';
    }

    // Whitespace first: without this, a token() call landing on a space
    // matches none of the patterns below and stream.eatWhile(/[^\s]/) below
    // would consume nothing either, returning a zero-length, non-advancing
    // token — CodeMirror requires that to change state to avoid looping
    // forever on it, and this tokenizer's state doesn't. Real infinite loop
    // in a real EditorView, not just a test-harness artifact.
    if (stream.eatSpace()) return null;

    if (stream.match('%%')) {
      stream.skipToEnd();
      return 'comment';
    }
    if (stream.match(STRING_PATTERN)) return 'string';
    if (stream.match(KEYWORD_PATTERN)) return 'keyword';
    if (stream.match(ARROW_PATTERN)) return 'operator';
    if (stream.match(BRACKET_PATTERN)) return 'bracket';

    if (!stream.eatWhile((ch) => !FALLBACK_STOP_PATTERN.test(ch))) {
      // The character at this position is itself a stop character (e.g. a
      // lone '-' or '=' that isn't part of a recognized arrow) — consume it
      // alone so the stream always advances.
      stream.next();
    }
    return null;
  },
};

/**
 * A `foldService` for `subgraph`/`end` blocks (SPEC.md §5). Depth-tracked so
 * nested `subgraph`s fold independently — an inner `end` closes the inner
 * block, not the outer one. Returns null (not foldable, no throw) for a
 * line that isn't a `subgraph` header, or for an unterminated `subgraph`
 * with no matching `end` anywhere in the rest of the document.
 */
export function foldMermaidSubgraph(
  state: EditorState,
  lineStart: number,
  lineEnd: number,
): { from: number; to: number } | null {
  const headerText = state.sliceDoc(lineStart, lineEnd);
  if (!/^\s*subgraph\b/.test(headerText)) return null;

  const doc = state.doc;
  const headerLineNumber = doc.lineAt(lineStart).number;
  let depth = 1;

  for (let lineNumber = headerLineNumber + 1; lineNumber <= doc.lines; lineNumber++) {
    const line = doc.line(lineNumber);
    const text = line.text.trim();
    if (/^subgraph\b/.test(text)) {
      depth++;
    } else if (/^end\b/.test(text)) {
      depth--;
      if (depth === 0) return { from: lineEnd, to: line.from };
    }
  }

  return null;
}
